import { useUser } from '@clerk/clerk-react';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import { ASSIGNED_TERRITORIES_API_URL } from '@/constants/apis';
import { configuration } from '@/constants/config';
import { apiManager } from '@/helpers/api.manager';
import { toast } from '@/hooks/ui/use-toast';
import { useTerritoryStore } from '@/stores/useTerritoryStore';
import { Territory } from '@/types/territories/territory.type';

import { TerritoryItem } from './territory-item';

const LikedTerritoriesByState = () => {
  const { user } = useUser();
  const { likedTerritories, setLikedTerritories } = useTerritoryStore();

  const [loadingTerritories, setLoadingTerritories] = useState(true);

  const fetchTerritories = useCallback(async () => {
    try {
      const response = await apiManager.get(ASSIGNED_TERRITORIES_API_URL, {
        baseURL: configuration.TERRITORIES_API_URL,
        headers: { 'Content-Type': 'application/json' },
        params: {
          zee_id: user?.id,
          liked_only: true,
        },
      });
      if (response.status !== 200) {
        toast({
          title: 'Error loading territories',
          variant: 'destructive',
        });
        return;
      }
      setLoadingTerritories(false);

      setLikedTerritories(response.data.data);
    } catch (error) {
      console.error('Error in fetching territories:', error);
    }
  }, [setLikedTerritories]);

  useEffect(() => {
    fetchTerritories();
  }, [fetchTerritories]);
  if (!likedTerritories)
    return (
      <div className="flex w-full justify-center">
        Currently No Liked Territories
      </div>
    );

  // Define priority mapping
  const statusOrder: Record<string, number> = {
    liked: 1,
    available: 2,
    pending: 3,
    reserved: 4,
    sold: 5,
  };

  // Helper to resolve effective status
  const getEffectiveStatus = (territory: Territory): string => {
    return territory.status.toLowerCase(); // available, pending, sold
  };

  // Group territories by state
  likedTerritories.sort((a, b) => {
    // Determine user ownership + priority
    const getUserPriority = (t: Territory) => {
      if (t.requested_by?.id === user?.id && t.status === 'PENDING') return 1;
      if (t.assigned_to?.id === user?.id && t.status === 'RESERVED') return 2;
      return 3; // all others
    };

    const priorityA = getUserPriority(a);
    const priorityB = getUserPriority(b);

    if (priorityA !== priorityB) return priorityA - priorityB;

    // fallback to status order for remaining
    const statusA = getEffectiveStatus(a);
    const statusB = getEffectiveStatus(b);

    return statusOrder[statusA] - statusOrder[statusB];
  });

  return (
    <div className="space-y-4 pb-20">
      {loadingTerritories ? (
        <div className="flex w-full justify-center">
          <Loader2 className="size-4 animate-spin" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border">
          {likedTerritories && (
            <>
              {likedTerritories.map((territory) => (
                <TerritoryItem
                  key={territory.id}
                  territory={territory}
                  isOwnedByUser={
                    user?.id == territory.assigned_to?.id ||
                    user?.id == territory.requested_by?.id
                  }
                />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export { LikedTerritoriesByState };
